/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult
} from "react-query";
import { useDispatch } from "react-redux";

import { ApiResponse } from "../../interfaces/Api";
import { notificationAdd } from "../../actions/notification";
import useCurrentResourceCentre from "../useCurrentResourceCentre";
import { TWardType, Ward } from "../../interfaces/WardInterface";
import { deleteWard, getWards, patchWard, postWard } from "../../api/wards";

export const wardsQueryKey = "wards";

// use wards
export const useWards = (options?: {
  type?: TWardType;
  active?: boolean;
}): UseQueryResult<Array<Ward>, Error> => {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery({
    queryKey: [wardsQueryKey, resourceCentre?.id, options?.type],
    queryFn: async () => {
      if (!resourceCentre?.id) return [];

      const response = await getWards({
        resourceCentreId: Number(resourceCentre.id),
        ...options
      });

      return response.data || [];
    },
    enabled: !!resourceCentre?.id
  });
};

// use post ward
export const usePostWard = (): UseMutationResult<
  ApiResponse<Ward[]>,
  Error,
  Partial<Ward>,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: (data: Partial<Ward>) =>
      postWard({ ...data, resourceCentreId: Number(resourceCentre?.id) }),

    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: [wardsQueryKey, resourceCentre?.id] });
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: response?.message || "Ward created successfully.",
          variant: "success"
        })
      );
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || error?.data?.message || "Ward creation failed.",
          variant: "error"
        })
      );
    }
  });
};

// use patch ward
export const usePatchWard = (): UseMutationResult<
  ApiResponse<Ward>,
  Error,
  { id: number | string; data: Partial<Ward> },
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: ({ id, data }) => patchWard({ id, data }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: [wardsQueryKey, resourceCentre?.id] });
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: response.message || "Ward updated successfully",
          variant: "success"
        })
      );
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to update room",
          variant: "error"
        })
      );
    }
  });
};

// use delete ward
export const useDeleteWard = (): UseMutationResult<
  ApiResponse<null>,
  Error,
  number | string,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: (id) => deleteWard(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: [wardsQueryKey, resourceCentre?.id] });
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: response?.message || "Ward deleted successfully",
          variant: "success"
        })
      );
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to delete room",
          variant: "error"
        })
      );
    }
  });
};
